import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashFeaturesEnum } from 'dash-common';
import {
  Button,
  Modal,
  ModalContext,
  PaginationProvider,
  UpgradeSubscriptionModalType,
  useCheckIfSubscriptionFeatureActiveBroker,
  useScreenResolution,
  ViewerContext,
} from 'dash-ui';

import { RoutesPath } from 'flow-components/routes/routes';
import { GetPropertyByIdQuery } from 'gql/generated.graphql';
import { useAddPropertyToTrack } from 'common/hooks/useAddPropertyToTrack/useAddPropertyToTrack';

import {
  ButtonsWrapper,
  ContainerWrapper,
} from './PropertyPreviewSidebar.styles';
import SidebarInfoContainer from './components/SidebarInfoContainer/SidebarInfoContainer';

const PropertyPreviewSidebar = ({
  property,
}: {
  property?: GetPropertyByIdQuery['getPropertyById'];
}) => {
  const { t } = useTranslation('pages');
  const { setPayload, toggleModal, setUpgradeSubscriptionModal } =
    useContext(ModalContext);
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { broker, account } = useContext(ViewerContext);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const { handleAddPropertyToTrack } = useAddPropertyToTrack();
  const { checkFeatureAccess } = useCheckIfSubscriptionFeatureActiveBroker([
    DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING,
  ]);

  return (
    <ContainerWrapper
      ml={[0, 6, 6, 6]}
      minWidth={[0, 215, 255, 255]}
      {...(!isMobile && { pos: 'sticky' })}
    >
      <Modal
        closeIcon
        title={t('propertyPreviewPage.moreInformation') || ''}
        isOpen={isModalOpened}
        onRequestClose={() => setIsModalOpened(false)}
      >
        <PaginationProvider perPage={10}>
          <SidebarInfoContainer property={property} />
        </PaginationProvider>
      </Modal>
      <ButtonsWrapper p={4}>
        {isMobile && (
          <Button
            p={2.5}
            onClick={() => setIsModalOpened(true)}
            variation="outline"
          >
            {t('common:labels.more')}
          </Button>
        )}
        <Button
          p={2.5}
          w={['fit-content', '100%', '100%', '100%']}
          mb={[0, 3, 3, 3]}
          icon={isMobile ? undefined : 'folder'}
          mx={[2, 0, 0, 0]}
          onClick={() => {
            setUpgradeSubscriptionModal({
              type: UpgradeSubscriptionModalType.PORTFOLIO,
            });
            if (checkFeatureAccess()) {
              setPayload({ propertyId: property?._id });
              toggleModal(true, 'AddPropertyToPortfolio');
            }
          }}
        >
          {t('propertyPage.addToPortfolio')}
        </Button>
        {!isMobile && (
          <Button
            p={2.5}
            w="100%"
            icon="bookmarkBorder"
            variation="outline"
            onClick={() => {
              handleAddPropertyToTrack(property || {}, account);
            }}
          >
            {t('pages:trackingPage.trackProperty')}
          </Button>
        )}
      </ButtonsWrapper>
      {!isMobile && (
        <PaginationProvider perPage={10}>
          <SidebarInfoContainer property={property} />
        </PaginationProvider>
      )}
    </ContainerWrapper>
  );
};

export default PropertyPreviewSidebar;
